import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import {
  Buildings, Sparkle, User, ClipboardText, CreditCard,
  UsersFour, ShieldCheck, PaintBrush, Database, Star, EnvelopeSimple, Shield, Clock,
  Calculator, GitBranch,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

import BranchesTab from "@/components/settings/BranchesTab";
import TherapyTypesTab from "@/components/settings/TherapyTypesTab";
import TemplatesTab from "@/components/settings/TemplatesTab";
import UsersTab from "@/components/settings/UsersTab";
import RelationshipsTab from "@/components/settings/RelationshipsTab";
import PaymentsTab from "@/components/settings/PaymentsTab";
import AuditSettingsTab from "@/components/settings/AuditSettingsTab";
import BrandingTab from "@/components/settings/BrandingTab";
import DataTab from "@/components/settings/DataTab";
import SubscriptionTab from "@/components/settings/SubscriptionTab";
import EmailSenderTab from "@/components/settings/EmailSenderTab";
import RolesTab from "@/components/settings/RolesTab";
import AttendanceSettingsTab from "@/components/settings/AttendanceSettingsTab";
import PayrollTemplatesTab from "@/components/settings/PayrollTemplatesTab";
import WorkflowApprovalsTab from "@/components/settings/WorkflowApprovalsTab";
import TherapyPlanTemplatesTab from "@/components/settings/TherapyPlanTemplatesTab";

const TABS = [
  { id: "branches",      label: "Branches",             short: "Branches",   icon: Buildings,     comp: BranchesTab },
  { id: "therapy-types", label: "Therapy types",        short: "Therapies",  icon: Sparkle,       comp: TherapyTypesTab },
  { id: "therapy-plan-templates", label: "Therapy Plan Templates", short: "Plans", icon: ClipboardText, comp: TherapyPlanTemplatesTab, ownerOrAdmin: true },
  { id: "templates",     label: "Assessment templates", short: "Templates",  icon: ClipboardText, comp: TemplatesTab },
  { id: "users",         label: "Users",                short: "Users",      icon: User,          comp: UsersTab },
  { id: "roles",         label: "Roles & Permissions",  short: "Roles",      icon: Shield,        comp: RolesTab },
  { id: "attendance",    label: "Attendance & Leave",   short: "Attendance", icon: Clock,         comp: AttendanceSettingsTab },
  { id: "payroll-templates", label: "Payroll Templates", short: "Payroll",   icon: Calculator,    comp: PayrollTemplatesTab },
  { id: "workflow",      label: "Workflow & Approvals", short: "Workflow",   icon: GitBranch,     comp: WorkflowApprovalsTab, ownerOrAdmin: true },
  { id: "relationships", label: "Relationships",        short: "Relations",  icon: UsersFour,     comp: RelationshipsTab },
  { id: "payments",      label: "Payments",             short: "Payments",   icon: CreditCard,    comp: PaymentsTab },
  { id: "email-sender",  label: "Email sender",         short: "Email",      icon: EnvelopeSimple, comp: EmailSenderTab, ownerOnly: true },
  { id: "subscription",  label: "Subscription",         short: "Plan",       icon: Star,          comp: SubscriptionTab, ownerOnly: true },
  { id: "branding",      label: "Branding",             short: "Branding",   icon: PaintBrush,    comp: BrandingTab },
  { id: "audit",         label: "Audit",                short: "Audit",      icon: ShieldCheck,   comp: AuditSettingsTab },
  { id: "data",          label: "Data",                 short: "Data",       icon: Database,      comp: DataTab, ownerOnly: true },
];

export default function SettingsPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isOwner = (user?.roles || []).includes("owner");
  const isAdmin = (user?.roles || []).includes("admin");
  const visibleTabs = TABS.filter(t => {
    if (t.ownerOnly && !isOwner) return false;
    if (t.ownerOrAdmin && !(isOwner || isAdmin)) return false;
    return true;
  });
  // iter-67e — Support `?tab={id}` deep-link so pages can navigate
  // directly to a specific settings tab (e.g. the Employees dialog's
  // Manage User button targets `?tab=users&highlight=...`).
  const requestedTab = searchParams.get("tab");
  const initialTab =
    (requestedTab && visibleTabs.find(t => t.id === requestedTab)?.id) ||
    visibleTabs[0].id;

  return (
    <div className="space-y-6 min-w-0">
      <div>
        <div className="overline mb-1.5">Settings</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Organization</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage branches, therapy services, and users.</p>
      </div>

      <Tabs defaultValue={initialTab}>
        {/* CSS Grid instead of flex-wrap — guarantees clean multi-line rows on
            every viewport and prevents any horizontal overflow. Grid cell
            widths ensure each trigger fills its cell without the shadcn
            `inline-flex` base class fighting us. */}
        <TabsList className="!grid !inline-flex-none grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-8 gap-1 h-auto w-full p-1">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                data-testid={`settings-${t.id}`}
                className="gap-1.5 min-w-0 w-full px-2 py-1.5 text-xs sm:text-sm"
              >
                <Icon size={13} weight="duotone" className="shrink-0" />
                <span className="truncate lg:hidden">{t.short}</span>
                <span className="truncate hidden lg:inline">{t.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {visibleTabs.map(t => {
          const Comp = t.comp;
          return (
            <TabsContent key={t.id} value={t.id} className="mt-4">
              <Comp />
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
